import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { ArrowLeft, Loader2, Sparkles, Heart, TrendingUp, Shield, RefreshCw, Flower2, MessageCircleHeart, Users, Leaf } from 'lucide-react';
import axios from 'axios';
import PremiumLock from '@/components/PremiumLock';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const categoryLabels = {
  daily: 'Daglig påminnelse',
  healing: 'Healing & Läkning',
  growth: 'Personlig utveckling',
  strength: 'Styrka & Mod',
  selfcare: 'Självomvårdnad',
  affirmation: 'Affirmationer',
  connection: 'Koppling & Relationer',
  peace: 'Frid & Lugn'
};

const categoryIcons = {
  daily: Sparkles,
  healing: Heart,
  growth: TrendingUp,
  strength: Shield,
  selfcare: Flower2,
  affirmation: MessageCircleHeart,
  connection: Users,
  peace: Leaf
};

const categoryColors = {
  daily: 'bg-amber-100/50',
  healing: 'bg-rose-100/50',
  growth: 'bg-emerald-100/50',
  strength: 'bg-violet-100/50',
  selfcare: 'bg-pink-100/50',
  affirmation: 'bg-sky-100/50',
  connection: 'bg-orange-100/50',
  peace: 'bg-teal-100/50'
};

export default function MotivationPage() {
  const navigate = useNavigate();
  const { hasAccess } = useAuth();
  
  const [motivations, setMotivations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMotivation, setSelectedMotivation] = useState(null);
  const [dailyMotivation, setDailyMotivation] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);

  useEffect(() => {
    if (hasAccess) {
      fetchMotivations();
      fetchDailyMotivation();
    } else {
      setLoading(false);
    }
  }, [hasAccess]);

  if (!hasAccess) {
    return (
      <div className="min-h-screen bg-background">
        <header className="border-b border-border/50 bg-card/50 backdrop-blur-sm sticky top-0 z-50">
          <div className="max-w-4xl mx-auto px-6 py-4 flex justify-between items-center">
            <Button variant="ghost" onClick={() => navigate('/category/thoughts')} className="rounded-full hover:bg-secondary/80">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Tillbaka
            </Button>
            <h1 className="font-heading text-xl font-light">Inspiration</h1>
            <div className="w-24" />
          </div>
        </header>
        <PremiumLock feature="Inspiration & Affirmationer" />
      </div>
    );
  }

  const fetchMotivations = async () => {
    try {
      const response = await axios.get(`${API}/motivation`);
      setMotivations(response.data);
    } catch (error) {
      toast.error('Kunde inte hämta inspirationer');
    } finally {
      setLoading(false);
    }
  };

  const fetchDailyMotivation = async () => {
    try {
      const response = await axios.get(`${API}/motivation/daily`);
      setDailyMotivation(response.data);
    } catch (error) {
      console.error('Could not fetch daily motivation');
    }
  };

  const categories = [...new Set(motivations.map(m => m.category))];
  const filteredMotivations = selectedCategory 
    ? motivations.filter(m => m.category === selectedCategory)
    : motivations;

  return (
    <div className="min-h-screen bg-background" data-testid="motivation-page">
      {/* Header */}
      <header className="border-b border-border/50 bg-card/50 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-6 py-4 flex justify-between items-center">
          <Button 
            variant="ghost" 
            onClick={() => selectedMotivation ? setSelectedMotivation(null) : navigate('/category/thoughts')}
            className="rounded-full hover:bg-secondary/80"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            {selectedMotivation ? 'Alla inspirationer' : 'Tillbaka'}
          </Button>
          <h1 className="font-heading text-xl font-light">Inspiration</h1>
          <div className="w-24" />
        </div>
      </header>

      {/* Main content */}
      <main className="max-w-4xl mx-auto px-6 py-12">
        {/* Motivation detail view */}
        {selectedMotivation ? (
          <div className="animate-fade-in space-y-8">
            {/* Header */}
            <div className={`${categoryColors[selectedMotivation.category]} rounded-2xl p-8 lg:p-12 text-center`}>
              {(() => {
                const Icon = categoryIcons[selectedMotivation.category];
                return (
                  <div className="w-16 h-16 mx-auto rounded-full bg-card flex items-center justify-center mb-6">
                    <Icon className="w-7 h-7 text-primary" />
                  </div>
                );
              })()}
              <span className="text-sm text-muted-foreground mb-4 block">
                {categoryLabels[selectedMotivation.category]}
              </span>
              <h2 className="font-heading text-3xl font-light mb-6">{selectedMotivation.title}</h2>
              <blockquote className="text-xl md:text-2xl font-heading font-light italic text-foreground leading-relaxed">
                "{selectedMotivation.quote}"
              </blockquote>
            </div>

            {/* Reflection */}
            <div className="bg-card rounded-2xl p-8 space-y-4">
              <h3 className="font-heading text-2xl">Reflektion</h3>
              <p className="text-muted-foreground leading-relaxed text-lg">
                {selectedMotivation.reflection}
              </p>
            </div>

            {/* Action */}
            <div className="bg-primary/10 rounded-2xl p-8 space-y-4">
              <h3 className="font-heading text-2xl">Din handling idag</h3>
              <p className="text-foreground leading-relaxed text-lg">
                {selectedMotivation.action}
              </p>
            </div>
          </div>
        ) : (
          <>
            {/* Intro */}
            <div className="text-center mb-8 space-y-3 animate-fade-in">
              <h2 className="font-heading text-3xl font-light">Inspiration & Motivation</h2>
              <p className="text-muted-foreground max-w-lg mx-auto">
                Daglig kraft för din resa mot ett bättre liv.
              </p>
            </div>

            {/* Daily motivation card */}
            {dailyMotivation && (
              <div className="bg-gradient-to-br from-primary/20 to-accent/30 rounded-2xl p-8 mb-8 animate-fade-in">
                <div className="flex items-center justify-between mb-4">
                  <span className="text-sm font-medium text-primary">Dagens inspiration</span>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={fetchDailyMotivation}
                    className="rounded-full h-8 w-8 p-0"
                    data-testid="refresh-daily"
                  >
                    <RefreshCw className="w-4 h-4" />
                  </Button>
                </div>
                <blockquote className="text-xl font-heading font-light italic mb-4">
                  "{dailyMotivation.quote}"
                </blockquote>
                <Button
                  variant="outline"
                  onClick={() => setSelectedMotivation(dailyMotivation)}
                  className="rounded-full"
                  data-testid="read-daily"
                >
                  Läs mer
                </Button>
              </div>
            )}

            {/* Category filter */}
            <div className="flex flex-wrap gap-2 justify-center mb-8">
              <Button
                variant={selectedCategory === null ? "default" : "outline"}
                onClick={() => setSelectedCategory(null)}
                className="rounded-full"
                data-testid="filter-all"
              >
                Alla
              </Button>
              {categories.map(cat => {
                const Icon = categoryIcons[cat];
                return (
                  <Button
                    key={cat}
                    variant={selectedCategory === cat ? "default" : "outline"}
                    onClick={() => setSelectedCategory(cat)}
                    className="rounded-full"
                    data-testid={`filter-${cat}`}
                  >
                    <Icon className="w-4 h-4 mr-2" />
                    {categoryLabels[cat]}
                  </Button>
                );
              })}
            </div>

            {/* Motivation list */}
            {loading ? (
              <div className="flex justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-primary/50" />
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {filteredMotivations.map((motivation, index) => {
                  const Icon = categoryIcons[motivation.category];
                  return (
                    <button
                      key={motivation.id}
                      onClick={() => setSelectedMotivation(motivation)}
                      className={`${categoryColors[motivation.category]} p-6 rounded-2xl text-left hover:shadow-soft transition-shadow duration-300 opacity-0 animate-slide-up group`}
                      style={{ animationDelay: `${index * 0.05}s` }}
                      data-testid={`motivation-card-${motivation.id}`}
                    >
                      <div className="flex items-start gap-4">
                        <div className="w-10 h-10 rounded-full bg-card flex items-center justify-center shrink-0 group-hover:-translate-y-1 transition-transform duration-300">
                          <Icon className="w-5 h-5 text-primary" />
                        </div>
                        <div className="flex-1">
                          <span className="text-xs text-muted-foreground">{categoryLabels[motivation.category]}</span>
                          <h3 className="font-heading text-lg font-normal mt-1">{motivation.title}</h3>
                          <p className="text-sm text-muted-foreground mt-2 line-clamp-2 italic">
                            "{motivation.quote.substring(0, 60)}..."
                          </p>
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </>
        )}
      </main>
    </div>
  );
}
